import { useState, useEffect } from 'react';
import { useSchemaDesignerStore } from '@/stores/schema-designer-store';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { RiSettings3Line, RiCheckLine } from '@remixicon/react';
import { toast } from 'sonner';

interface EditTableDialogProps {
  tableId: string;
  tableName: string;
  tableComment?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const EditTableDialog: React.FC<EditTableDialogProps> = ({
  tableId,
  tableName: initialTableName,
  tableComment: initialTableComment,
  open,
  onOpenChange,
}) => {
  const [tableName, setTableName] = useState(initialTableName);
  const [tableComment, setTableComment] = useState(initialTableComment || '');
  const { updateTable, nodes } = useSchemaDesignerStore();

  // Update state when props change
  useEffect(() => {
    setTableName(initialTableName);
    setTableComment(initialTableComment || '');
  }, [initialTableName, initialTableComment, open]);

  const handleUpdateTable = () => {
    const trimmedName = tableName.trim();

    if (!trimmedName) {
      toast.error('Table name is required');
      return;
    }

    // Check if new table name already exists (but allow same name)
    if (
      trimmedName.toLowerCase() !== initialTableName.toLowerCase() &&
      nodes.some((node) => node.data.name.toLowerCase() === trimmedName.toLowerCase())
    ) {
      toast.error(`Table "${trimmedName}" already exists`);
      return;
    }

    // Validate table name format
    if (!/^[a-zA-Z_][a-zA-Z0-9_]*$/.test(trimmedName)) {
      toast.error('Table name must start with a letter or underscore and contain only letters, numbers, and underscores');
      return;
    }

    updateTable(tableId, {
      name: trimmedName,
      comment: tableComment.trim() || undefined,
    });

    toast.success(`Table "${trimmedName}" updated`);
    onOpenChange(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey && tableName.trim()) {
      e.preventDefault();
      handleUpdateTable();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RiSettings3Line className="h-5 w-5 text-primary" />
            Edit Table Settings
          </DialogTitle>
          <DialogDescription>
            Update the table name and description. Changes will be reflected in the generated SQL.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="editTableName" className="text-sm font-medium">
              Table Name <span className="text-destructive">*</span>
            </Label>
            <Input
              id="editTableName"
              placeholder="e.g., users, products, orders"
              value={tableName}
              onChange={(e) => setTableName(e.target.value)}
              onKeyDown={handleKeyDown}
              autoFocus
              className="font-mono"
            />
            <p className="text-xs text-muted-foreground">
              Use lowercase with underscores (e.g., user_profiles)
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="editTableComment" className="text-sm font-medium">
              Comment <span className="text-muted-foreground">(optional)</span>
            </Label>
            <Textarea
              id="editTableComment"
              placeholder="Brief description of this table..."
              value={tableComment}
              onChange={(e) => setTableComment(e.target.value)}
              rows={3}
              className="resize-none"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleUpdateTable} disabled={!tableName.trim()} className="gap-2">
            <RiCheckLine className="h-4 w-4" />
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
